/**
 * New Help Manager
 *
 * Client for the git-backed "new help" API (`/portal/new-help/{slug}/`).
 * The portal is implied by the deployment (no portal segment in the URL).
 * Documents are addressed by a slug that mirrors the frontend route path,
 * e.g. the page at `/account/users` resolves to the slug `account/users`.
 *
 * Available Functions:
 * - fetchNewHelpList(): Fetch (and cache) the list of available document slugs for the portal
 * - resolveNewHelpSlug(): Resolve a route path to a known slug, falling back to the nearest `index` document
 * - fetchNewHelpDocument(): Fetch a single document's content by slug, or null if it doesn't exist
 */

import apiFetch, { FetchApiError } from './apiFetch';
import { getSessionStorage, setSessionStorage } from './sessionStorage';

const CACHE_PREFIX = 'new-help:';
const CACHE_TTL_MS = 60 * 60 * 1000;

export type NewHelpDocument = {
    portal: string;
    slug: string;
    branch: string;
    path: string;
    source: string;
    sha: string;
    last_modified: string | null;
    markdown: string;
};

export type NewHelpListItem = {
    slug: string;
    path: string;
    sha: string;
    source: string;
};

type CachedNewHelp = {
    last_checked: string;
    document: NewHelpDocument | null;
};

type CachedNewHelpList = {
    last_checked: string;
    items: NewHelpListItem[];
};

const LIST_CACHE_KEY = `${CACHE_PREFIX}list`;

const cacheKey = (slug: string) => `${CACHE_PREFIX}${slug}`;

const getCached = (slug: string): CachedNewHelp | null => {
    const cached = getSessionStorage(cacheKey(slug)) as CachedNewHelp | null;

    if (!cached?.last_checked) return null;

    const age = Date.now() - new Date(cached.last_checked).getTime();

    if (Number.isNaN(age) || age > CACHE_TTL_MS) return null;

    return cached;
};

const setCached = (slug: string, document: NewHelpDocument | null) => {
    setSessionStorage(cacheKey(slug), {
        last_checked: new Date().toISOString(),
        document,
    });
};

/**
 * Fetch a single new-help document by slug.
 * Results (including "not found") are cached in sessionStorage for an hour.
 *
 * @returns The resolved document, or null if it doesn't exist / the request failed.
 */
export const fetchNewHelpDocument = async (slug: string): Promise<NewHelpDocument | null> => {
    const cached = getCached(slug);

    if (cached) return cached.document;

    try {
        const response = await apiFetch(`/portal/new-help/${slug}/`);
        const document = (await response.json()) as NewHelpDocument;

        setCached(slug, document);
        return document;
    } catch (error) {
        if (error instanceof FetchApiError && error.status === 404) {
            setCached(slug, null);
            return null;
        }

        // eslint-disable-next-line no-console
        console.error('Failed to fetch new help document:', error);
        return null;
    }
};

const getCachedList = (): NewHelpListItem[] | null => {
    const cached = getSessionStorage(LIST_CACHE_KEY) as CachedNewHelpList | null;

    if (!cached?.last_checked) return null;

    const age = Date.now() - new Date(cached.last_checked).getTime();

    if (Number.isNaN(age) || age > CACHE_TTL_MS) return null;

    return cached.items;
};

const setCachedList = (items: NewHelpListItem[]) => {
    setSessionStorage(LIST_CACHE_KEY, {
        last_checked: new Date().toISOString(),
        items,
    });
};

// Dedupes concurrent list fetches (e.g. multiple pages mounting before the first request resolves).
let listPromise: Promise<NewHelpListItem[]> | null = null;

/**
 * Fetch the list of help document slugs available to the portal. This is a
 * public endpoint (no auth, no per-document content), so it's cheap to fetch
 * once and cache for the session — used to check help availability instantly
 * without a round trip on every page.
 */
export const fetchNewHelpList = async (): Promise<NewHelpListItem[]> => {
    const cached = getCachedList();

    if (cached) return cached;

    listPromise ??= (async () => {
        try {
            const response = await apiFetch('/portal/new-help/');
            const data = (await response.json()) as { documents: NewHelpListItem[] };

            setCachedList(data.documents);
            return data.documents;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch new help list:', error);
            return [];
        } finally {
            listPromise = null;
        }
    })();

    return listPromise;
};

/**
 * Resolve the slug to use for a route path against a document list: the exact
 * slug if it exists, otherwise the nearest `index` document
 * (e.g. `account/users` -> `account/index`, `accreditation` -> `accreditation/index`).
 * Returns null if neither exists.
 *
 * @param pathname - A route path, e.g. `/account/users`.
 */
export const resolveNewHelpSlug = (pathname: string, list: NewHelpListItem[]): string | null => {
    const slug = pathname.replace(/^\/+|\/+$/g, '');

    if (!slug) return null;

    const slugs = new Set(list.map((item) => item.slug));

    if (slugs.has(slug)) return slug;

    const segments = slug.split('/');
    const indexSlug =
        segments.length > 1
            ? [...segments.slice(0, -1), 'index'].join('/')
            : `${slug}/index`;

    if (indexSlug !== slug && slugs.has(indexSlug)) return indexSlug;

    return null;
};

/**
 * Derive a human-readable title from a resolved slug, e.g. `account/users` -> `Users`,
 * `account/index` -> `Account`.
 */
export const titleFromSlug = (slug: string): string => {
    const segments = slug.split('/').filter(Boolean);
    const relevant = segments.length > 1 && segments.at(-1) === 'index'
        ? segments.at(-2)
        : segments.at(-1);

    return (relevant || slug)
        .split(/[-_]/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
